const cylinderKeyCache = new Map();
const CANONICAL_CYLINDER_KEY_COUNT = 16;

export const CANONICAL_SCHEMA_VERSION = "canonical-flight-points/v1";
export const FLIGHT_EVIDENCE_PACKET_SCHEMA_VERSION = "flight-evidence-packet/v1";
// v16: evidence packet summaries use the same endpoint-trimmed points as analysis and charts.
// v18: metric entries keep the detector's own observation (detectorNote)
//      when the sensitivity label replaced it.
// v17: findings carry their detector's own sentence, measurements, and evidence
//      window instead of a score stub; detector arrays keep their items instead
//      of collapsing to a count; the per-metric detail budget holds a whole
//      curated story; flagged windows are severity-ranked before the cap; and
//      contextDeltas compares the window against the flight instead of
//      duplicating stats.
export const FLIGHT_EVIDENCE_GENERATOR_VERSION = "flight-evidence-generator/v23";
export const FLIGHT_AI_SUMMARY_SCHEMA_VERSION = "flight-ai-summary/v1";
export const FLIGHT_AI_SUMMARY_GENERATOR_VERSION = "flight-ai-summary-generator/v1";
export const FLIGHT_AI_SUMMARY_PROMPT_VERSION = "flight-ai-summary-prompt/v28";
export const PARSER_CONFIDENCE_LEVELS = Object.freeze(["Low", "Medium", "High"]);
export const PARSER_WARNING_SEVERITIES = Object.freeze(["info", "warning", "error"]);
export const CANONICAL_ENGINE_LABEL_MODES = Object.freeze(["left-right", "front-rear"]);
export const CANONICAL_ENGINE_FAMILIES = Object.freeze(["air-cooled-piston", "rotax", "unknown"]);
export const CANONICAL_ENGINE_COOLING_TYPES = Object.freeze(["air", "liquid", "mixed", "unknown"]);
export const CANONICAL_ENGINE_CONTROL_TYPES = Object.freeze(["ecu", "manual-mixture", "unknown"]);
export const CANONICAL_COMBO_ALIGNMENTS = Object.freeze(["rpm-edge", "rpm-correlation", "clock", "unaligned"]);
export const EVIDENCE_FINDING_SCOPES = Object.freeze(["aircraft", "engine", "comparison", "context"]);
export const EVIDENCE_SEVERITIES = Object.freeze(["info", "watch", "review"]);
export const EVIDENCE_WINDOW_SOURCES = Object.freeze(["finding-window", "metric-detail", "derived-context"]);

export const CANONICAL_ENGINE_INDEXED_FIELD_ALIASES = Object.freeze({
  fuelFlowLeft: "fuelFlow1",
  fuelFlowRight: "fuelFlow2",
  fuelUsedLeft: "fuelUsed1",
  fuelUsedRight: "fuelUsed2",
  ampsLeft: "amps1",
  ampsRight: "amps2",
  oilTempLeft: "oilTemp1",
  oilTempRight: "oilTemp2",
  oilPressureLeft: "oilPressure1",
  oilPressureRight: "oilPressure2",
  rpmLeft: "rpm1",
  rpmRight: "rpm2",
  mapLeft: "map1",
  mapRight: "map2",
});

export const CANONICAL_ENGINE_INDEXED_FIELD_LEGACY_ALIASES = Object.freeze(
  Object.fromEntries(Object.entries(CANONICAL_ENGINE_INDEXED_FIELD_ALIASES).map(([legacy, indexed]) => [indexed, legacy])),
);

const indexedEngineNumberFields = Object.freeze([
  "powerRopHp1", "powerRopHp2", "percentPowerRop1", "percentPowerRop2",
  "powerLopHp1", "powerLopHp2", "percentPowerLop1", "percentPowerLop2",
  "fuelPressure1",
  "fuelPressure2",
  "fuelFlow1",
  "fuelFlow2",
  "fuelUsed1",
  "fuelUsed2",
  "amps1",
  "amps2",
  "oilTemp1",
  "oilTemp2",
  "oilPressure1",
  "oilPressure2",
  "rpm1",
  "rpm2",
  "map1",
  "map2",
]);
const legacyEngineNumberFields = Object.freeze(Object.keys(CANONICAL_ENGINE_INDEXED_FIELD_ALIASES));
const timelineFields = Object.freeze(["elapsedSeconds", "displayTime"]);
const optionalNumberFields = Object.freeze([
  "t",
  "egt",
  ...cylinderKeys("egt"),
  "cht",
  ...cylinderKeys("cht"),
  "fuelFlow",
  "fuelFlow1",
  "fuelFlow2",
  "fuelUsed",
  "fuelUsed1",
  "fuelUsed2",
  "fuelPressure",
  "fuelPressure1",
  "fuelPressure2",
  "fuelLeft",
  "fuelRight",
  "coolantTemp",
  "coolantPressure",
  "buss",
  "amps",
  "amps1",
  "amps2",
  "alternatorAmps",
  "batteryAmps",
  "oat",
  "carbTemp",
  "oilTemp",
  "oilTemp1",
  "oilTemp2",
  "oilPressure",
  "oilPressure1",
  "oilPressure2",
  "rpm",
  "rpm1",
  "rpm2",
  "map",
  "map1",
  "map2",
  "tit",
  "tit1",
  "tit2",
  "upperDeckPressure",
  "inductionTemp",
  "compressorDischargeTemp",
  "manifoldTemp",
  "boostPressure",
  "throttlePosition",
  "dynonCanBoostPressure",
  "dynonCanEngineTime",
  "dynonCanBoostPressureError",
  "dynonCanOilPressureCompensation",
  "dynonCanTargetWastegatePosition",
  "dynonCanAmbientEngineTemp",
  "dynonCanAmbientEnginePressure",
  "ias",
  "tas",
  "groundSpeed",
  "percentPower",
  "powerRopHp",
  "powerRopHp1", "powerRopHp2", "percentPowerRop1", "percentPowerRop2",
  "powerLopHp1", "powerLopHp2", "percentPowerLop1", "percentPowerLop2",
  "percentPowerRop",
  "powerLopHp",
  "percentPowerLop",
  "altitude",
  "densityAltitude",
  "pressureAltitude",
  "baroPressure",
  "verticalSpeed",
  "pitch",
  "roll",
  "latitude",
  "longitude",
  "gpsTrack",
  "gpsAltitude",
  "rawSampleCount",
  ...legacyEngineNumberFields,
]);
const optionalTextFields = Object.freeze([
  "timestamp",
  "utcDisplayTime",
  "phase",
  "phaseConfidence",
  "phaseEvidence",
  "phaseSource",
  "loggedPhase",
  "egtLeaningState",
  "gpsFixQuality",
  "gpsSource",
  "ecuLaneAStatus",
  "ecuLaneBStatus",
  "ecuStatus",
  "ecuFaultCode",
]);
const optionalBooleanFields = Object.freeze([
  "elapsedTimeEstimated",
  "onGround",
  "weightOnWheels",
]);
const pointMetadataFields = Object.freeze(["parserConfidence", "parserWarnings"]);
const knownPointFields = new Set([
  "sourceSampleIndices",
  ...timelineFields,
  ...optionalNumberFields,
  ...optionalTextFields,
  ...optionalBooleanFields,
  ...pointMetadataFields,
]);
const confidenceLevelSet = new Set(PARSER_CONFIDENCE_LEVELS);
const parserWarningSeveritySet = new Set(PARSER_WARNING_SEVERITIES);
const canonicalEngineFamilySet = new Set(CANONICAL_ENGINE_FAMILIES);
const canonicalEngineCoolingTypeSet = new Set(CANONICAL_ENGINE_COOLING_TYPES);
const canonicalEngineControlTypeSet = new Set(CANONICAL_ENGINE_CONTROL_TYPES);
const canonicalComboAlignmentSet = new Set(CANONICAL_COMBO_ALIGNMENTS);
const evidenceFindingScopeSet = new Set(EVIDENCE_FINDING_SCOPES);
const evidenceSeveritySet = new Set(EVIDENCE_SEVERITIES);
const evidenceWindowSourceSet = new Set(EVIDENCE_WINDOW_SOURCES);

// Channels that make a log an engine log. A parsed flight with no finite value
// in any of these has nothing the analyzers can score, so ingest refuses it.
// Deliberately excludes electrical (buss/amps), OAT, and fuel quantity:
// airframe/air-data exports (e.g. Garmin TXi GDU logs) carry those alongside
// an empty engine column template.
const engineDataPointFields = Object.freeze([
  "fuelPressure1",
  "fuelPressure2",
  "egt",
  ...cylinderKeys("egt"),
  "cht",
  ...cylinderKeys("cht"),
  "tit",
  "tit1",
  "tit2",
  "oilTemp",
  "oilTemp1",
  "oilTemp2",
  "oilPressure",
  "oilPressure1",
  "oilPressure2",
  "fuelFlow",
  "fuelFlow1",
  "fuelFlow2",
  "fuelPressure",
  "rpm",
  "rpm1",
  "rpm2",
  "map",
  "map1",
  "map2",
]);

export const CANONICAL_TIMELINE_FIELDS = timelineFields;
export const CANONICAL_ENGINE_INDEXED_NUMBER_FIELDS = indexedEngineNumberFields;
export const CANONICAL_ENGINE_LEGACY_NUMBER_FIELDS = legacyEngineNumberFields;
export const CANONICAL_OPTIONAL_NUMBER_FIELDS = optionalNumberFields;
export const CANONICAL_OPTIONAL_TEXT_FIELDS = optionalTextFields;
export const CANONICAL_OPTIONAL_BOOLEAN_FIELDS = optionalBooleanFields;
export const CANONICAL_POINT_METADATA_FIELDS = pointMetadataFields;
export const CANONICAL_POINT_FIELDS = Object.freeze([...knownPointFields]);
export const CANONICAL_ENGINE_DATA_POINT_FIELDS = engineDataPointFields;

export function cylinderKeys(prefix) {
  if (!cylinderKeyCache.has(prefix)) {
    cylinderKeyCache.set(prefix, Object.freeze(Array.from({ length: CANONICAL_CYLINDER_KEY_COUNT }, (_, index) => `${prefix}${index + 1}`)));
  }
  return cylinderKeyCache.get(prefix);
}

export function cylinderNumberFromKey(key) {
  return Number(String(key).match(/\d+$/)?.[0]) || null;
}

export function canonicalFieldForKey(key) {
  return CANONICAL_ENGINE_INDEXED_FIELD_ALIASES[key] ?? key;
}

export function canonicalValueForField(point, key) {
  if (!point || key == null) return undefined;
  if (point[key] !== undefined) return point[key];
  const legacyKey = CANONICAL_ENGINE_INDEXED_FIELD_LEGACY_ALIASES[key];
  if (legacyKey && point[legacyKey] !== undefined) return point[legacyKey];
  const indexedKey = CANONICAL_ENGINE_INDEXED_FIELD_ALIASES[key];
  if (indexedKey && point[indexedKey] !== undefined) return point[indexedKey];
  return undefined;
}

export function pointsHaveEngineData(points) {
  if (!Array.isArray(points)) return false;
  return points.some((point) => point
    && engineDataPointFields.some((field) => Number.isFinite(canonicalValueForField(point, field))));
}

function addIssue(issues, path, message, code = "invalid_field") {
  issues.push({ path, message, code });
}

function isPlainObject(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function isValidStringOrNull(value) {
  return value === null || typeof value === "string";
}

function validateNumberField(value, path, errors, { required = false } = {}) {
  if (value === undefined) {
    if (required) addIssue(errors, path, "is required", "missing_required_field");
    return;
  }
  if (value === null && !required) return;
  if (!Number.isFinite(value)) {
    addIssue(errors, path, "must be a finite number");
  }
}

function validateTextField(value, path, errors, { required = false } = {}) {
  if (value === undefined) {
    if (required) addIssue(errors, path, "is required", "missing_required_field");
    return;
  }
  if (required && typeof value !== "string") {
    addIssue(errors, path, "must be a string");
    return;
  }
  if (!required && !isValidStringOrNull(value)) {
    addIssue(errors, path, "must be a string or null");
  }
}

function validateBooleanField(value, path, errors) {
  if (value === undefined || value === null) return;
  if (typeof value !== "boolean") {
    addIssue(errors, path, "must be a boolean or null");
  }
}

export function validateParserWarning(warning, path = "parserWarnings[]") {
  const errors = [];
  if (!isPlainObject(warning)) {
    addIssue(errors, path, "must be an object");
    return { valid: false, errors };
  }
  for (const key of ["code", "message", "severity"]) {
    if (typeof warning[key] !== "string" || !warning[key].trim()) {
      addIssue(errors, `${path}.${key}`, "must be a non-empty string");
    }
  }
  if (typeof warning.severity === "string" && !parserWarningSeveritySet.has(warning.severity)) {
    addIssue(errors, `${path}.severity`, `must be one of ${PARSER_WARNING_SEVERITIES.join(", ")}`);
  }
  if (warning.rowIndex !== undefined && (!Number.isInteger(warning.rowIndex) || warning.rowIndex < 0)) {
    addIssue(errors, `${path}.rowIndex`, "must be a non-negative integer");
  }
  if (warning.field !== undefined && typeof warning.field !== "string") {
    addIssue(errors, `${path}.field`, "must be a string");
  }
  return { valid: errors.length === 0, errors };
}

function validateParserWarnings(value, path, errors) {
  if (value === undefined) return;
  if (!Array.isArray(value)) {
    addIssue(errors, path, "must be an array");
    return;
  }
  value.forEach((warning, index) => {
    errors.push(...validateParserWarning(warning, `${path}[${index}]`).errors);
  });
}

function validatePositiveIntegerArray(value, path, errors) {
  if (!Array.isArray(value)) {
    addIssue(errors, path, "must be an array");
    return;
  }
  value.forEach((item, index) => {
    if (!Number.isInteger(item) || item <= 0) {
      addIssue(errors, `${path}[${index}]`, "must be a positive integer");
    }
  });
}

function validateEngineLayoutGroups(value, path, errors) {
  if (value === undefined) return;
  if (!Array.isArray(value)) {
    addIssue(errors, path, "must be an array of engine groups");
    return;
  }
  value.forEach((group, index) => validatePositiveIntegerArray(group, `${path}[${index}]`, errors));
}

function validateEngineLayout(value, path, errors) {
  if (value === undefined) return;
  if (!isPlainObject(value)) {
    addIssue(errors, path, "must be an object");
    return;
  }
  const count = Number(value.count ?? value.engineCount);
  if (!Number.isInteger(count) || count <= 0) {
    addIssue(errors, `${path}.count`, "must be a positive integer");
  }
  if (value.cylindersPerEngine !== undefined) {
    validatePositiveIntegerArray(value.cylindersPerEngine, `${path}.cylindersPerEngine`, errors);
  }
  if (value.engineIds !== undefined) {
    if (!Array.isArray(value.engineIds)) {
      addIssue(errors, `${path}.engineIds`, "must be an array");
    } else {
      value.engineIds.forEach((id, index) => {
        if (typeof id !== "string" || !id.trim()) {
          addIssue(errors, `${path}.engineIds[${index}]`, "must be a non-empty string");
        }
      });
    }
  }
  if (value.labelMode !== undefined && !CANONICAL_ENGINE_LABEL_MODES.includes(value.labelMode)) {
    addIssue(errors, `${path}.labelMode`, `must be one of ${CANONICAL_ENGINE_LABEL_MODES.join(", ")}`);
  }
  validateEngineLayoutGroups(value.egtGroups, `${path}.egtGroups`, errors);
  validateEngineLayoutGroups(value.chtGroups, `${path}.chtGroups`, errors);
}

function parserWarningErrorMessage(result) {
  return result.errors.map((error) => `${error.path} ${error.message}`).join("; ");
}

export interface ParserWarningInput {
  code?: unknown;
  message?: unknown;
  severity?: string;
  rowIndex?: unknown;
  field?: unknown;
}

export function createParserWarning({
  code,
  message,
  severity = "warning",
  rowIndex,
  field,
}: ParserWarningInput = {}) {
  const warning: Record<string, unknown> = {
    code,
    message,
    severity,
  };
  if (rowIndex !== undefined) warning.rowIndex = rowIndex;
  if (field !== undefined) warning.field = field;

  const result = validateParserWarning(warning, "parserWarning");
  if (!result.valid) {
    throw new Error(`Invalid parser warning: ${parserWarningErrorMessage(result)}`);
  }
  return Object.freeze(warning);
}

export interface CanonicalEnvelopeInput {
  parserId?: unknown;
  parserVersion?: unknown;
  parserConfidence?: string;
  parserWarnings?: unknown[];
  engineLayout?: unknown;
  engineFamily?: unknown;
  engineMake?: unknown;
  engineModel?: unknown;
  engineCooling?: unknown;
  engineControl?: unknown;
  cgrTwinAlignment?: unknown;
  comboAlignment?: unknown;
  comboOffsetSeconds?: number;
  sourceData?: unknown;
  auxSeriesKeys?: unknown;
  schemaVersion?: string;
}

export function createCanonicalParserEnvelope(points, {
  parserId,
  parserVersion,
  parserConfidence = "Medium",
  parserWarnings = [],
  engineLayout,
  engineFamily,
  engineMake,
  engineModel,
  engineCooling,
  engineControl,
  cgrTwinAlignment,
  comboAlignment,
  comboOffsetSeconds,
  sourceData,
  auxSeriesKeys,
  schemaVersion = CANONICAL_SCHEMA_VERSION,
}: CanonicalEnvelopeInput = {}) {
  const envelope: Record<string, unknown> = {
    schemaVersion,
    parserId,
    parserVersion,
    parserConfidence,
    parserWarnings,
    points,
  };
  if (cgrTwinAlignment !== undefined) envelope.cgrTwinAlignment = cgrTwinAlignment;
  if (engineLayout !== undefined) envelope.engineLayout = engineLayout;
  if (engineFamily !== undefined) envelope.engineFamily = engineFamily;
  if (engineMake !== undefined) envelope.engineMake = engineMake;
  if (engineModel !== undefined) envelope.engineModel = engineModel;
  if (engineCooling !== undefined) envelope.engineCooling = engineCooling;
  if (engineControl !== undefined) envelope.engineControl = engineControl;
  if (comboAlignment !== undefined) envelope.comboAlignment = comboAlignment;
  if (comboOffsetSeconds !== undefined) envelope.comboOffsetSeconds = comboOffsetSeconds;
  if (sourceData !== undefined) envelope.sourceData = sourceData;
  if (auxSeriesKeys !== undefined) envelope.auxSeriesKeys = auxSeriesKeys;
  const result = validateCanonicalFlightPoints(envelope);
  if (!result.valid) {
    throw new Error(`Invalid canonical parser envelope: ${parserWarningErrorMessage(result)}`);
  }
  return envelope;
}

export function validateCanonicalFlightPoint(point, context: { path?: string } = {}) {
  const errors = [];
  const warnings = [];
  const path = context.path ?? "points[]";

  if (!isPlainObject(point)) {
    addIssue(errors, path, "must be an object");
    return { valid: false, errors, warnings };
  }

  validateNumberField(point.elapsedSeconds, `${path}.elapsedSeconds`, errors, { required: true });
  validateTextField(point.displayTime, `${path}.displayTime`, errors, { required: true });

  for (const key of optionalNumberFields) {
    if (key === "elapsedSeconds") continue;
    validateNumberField(point[key], `${path}.${key}`, errors);
  }
  for (const key of optionalTextFields) {
    validateTextField(point[key], `${path}.${key}`, errors);
  }
  for (const key of optionalBooleanFields) {
    validateBooleanField(point[key], `${path}.${key}`, errors);
  }
  if (point.phaseConfidence !== undefined && point.phaseConfidence !== null && !confidenceLevelSet.has(point.phaseConfidence)) {
    addIssue(errors, `${path}.phaseConfidence`, `must be one of ${PARSER_CONFIDENCE_LEVELS.join(", ")}`);
  }
  if (point.parserConfidence !== undefined && point.parserConfidence !== null && !confidenceLevelSet.has(point.parserConfidence)) {
    addIssue(errors, `${path}.parserConfidence`, `must be one of ${PARSER_CONFIDENCE_LEVELS.join(", ")}`);
  }
  validateParserWarnings(point.parserWarnings, `${path}.parserWarnings`, errors);
  if (point.sourceSampleIndices !== undefined && (!Array.isArray(point.sourceSampleIndices)
    || point.sourceSampleIndices.some((index) => !Number.isSafeInteger(index) || index < 0))) {
    addIssue(errors, path + ".sourceSampleIndices", "must contain nonnegative integer indices");
  }

  for (const key of Object.keys(point)) {
    if (!knownPointFields.has(key)) {
      addIssue(warnings, `${path}.${key}`, "is not part of the canonical schema yet", "unknown_field");
    }
  }

  return { valid: errors.length === 0, errors, warnings };
}

function validateSourceData(sourceData, points, errors) {
  if (sourceData === undefined) return;
  if (!isPlainObject(sourceData) || sourceData.version !== "flight-source-data/v1") {
    addIssue(errors, "sourceData", "must be a flight-source-data/v1 object");
    return;
  }
  if (!Array.isArray(sourceData.samples) || sourceData.samples.some((sample) => !isPlainObject(sample))) {
    addIssue(errors, "sourceData.samples", "must be an array of original mapped samples");
    return;
  }
  if (!Array.isArray(sourceData.transformations)) addIssue(errors, "sourceData.transformations", "must be an array");
  if (!isPlainObject(sourceData.aggregationPolicy)) addIssue(errors, "sourceData.aggregationPolicy", "must declare field policies");
  if (!Array.isArray(sourceData.documents) || !sourceData.documents.length) {
    addIssue(errors, "sourceData.documents", "must retain at least one source");
  } else sourceData.documents.forEach((document, documentIndex) => {
    const path = "sourceData.documents[" + documentIndex + "]";
    if (!isPlainObject(document)) { addIssue(errors, path, "must be an object"); return; }
    if (document.format === "csv") {
      if (!Array.isArray(document.records) || document.records.some((row) => !Array.isArray(row) || row.some((cell) => typeof cell !== "string"))) {
        addIssue(errors, path + ".records", "must retain positional string cells");
        return;
      }
      if (!Array.isArray(document.recordIndices) || document.recordIndices.length !== document.records.length
        || document.recordIndices.some((index, i) => !Number.isSafeInteger(index) || index < 0 || (i > 0 && index <= document.recordIndices[i - 1]))) {
        addIssue(errors, path + ".recordIndices", "must identify every retained source record in order");
      }
      if (!Array.isArray(document.columns) || document.records.some((row) => row.length > document.columns.length)) {
        addIssue(errors, path + ".columns", "must account for every positional column");
      } else document.columns.forEach((column, index) => {
        if (column?.id !== "column:" + index || column.index !== index || typeof column.name !== "string"
          || column.coverage !== "preserved-as-source" || !Number.isSafeInteger(column.populatedCount) || column.populatedCount < 0) {
          addIssue(errors, path + ".columns[" + index + "]", "must provide stable identity and retention coverage");
        }
      });
    } else if (document.format === "jpi-binary") {
      if (document.encoding !== "base64" || typeof document.data !== "string"
        || document.data.length % 4 !== 0 || !/^[A-Za-z0-9+/]*={0,2}$/.test(document.data)) {
        addIssue(errors, path, "must retain base64 source bytes");
      }
    } else if (document.format === "jpi-decoded") {
      if (!Array.isArray(document.records) || document.records.some((row) => !isPlainObject(row))) addIssue(errors, path + ".records", "must retain decoded records");
    } else addIssue(errors, path + ".format", "must identify a supported source representation");
  });
  points.forEach((point, index) => {
    if (!Array.isArray(point?.sourceSampleIndices) || !point.sourceSampleIndices.length
      || point.sourceSampleIndices.some((sample) => !Number.isSafeInteger(sample) || sample < 0 || sample >= sourceData.samples.length)) {
      addIssue(errors, "points[" + index + "].sourceSampleIndices", "must reference retained original mapped samples");
    }
    if (point?.elapsedSeconds < 0 || (index > 0 && point?.elapsedSeconds < points[index - 1]?.elapsedSeconds)) {
      addIssue(errors, "points[" + index + "].elapsedSeconds", "must be a nonnegative monotonic analysis timeline");
    }
  });
}

export function validateCanonicalFlightPoints(output, options: {
  schemaVersion?: string;
  parserId?: unknown;
  parserVersion?: unknown;
  path?: string;
} = {}) {
  const errors = [];
  const warnings = [];
  const isEnvelope = isPlainObject(output) && Array.isArray(output.points);
  const points = Array.isArray(output) ? output : isEnvelope ? output.points : null;
  const schemaVersion = isEnvelope
    ? output.schemaVersion
    : options.schemaVersion ?? CANONICAL_SCHEMA_VERSION;

  if (!points) {
    addIssue(errors, "output", "must be an array of points or an object with a points array", "invalid_output");
    return {
      valid: false,
      schemaVersion: schemaVersion ?? CANONICAL_SCHEMA_VERSION,
      parserId: options.parserId ?? null,
      parserVersion: options.parserVersion ?? null,
      errors,
      warnings,
    };
  }

  if (isEnvelope) {
    if (output.schemaVersion !== CANONICAL_SCHEMA_VERSION) {
      addIssue(errors, "schemaVersion", `must be ${CANONICAL_SCHEMA_VERSION}`);
    }
    validateTextField(output.parserId, "parserId", errors, { required: true });
    validateTextField(output.parserVersion, "parserVersion", errors, { required: true });
    if (output.parserConfidence !== undefined && !confidenceLevelSet.has(output.parserConfidence)) {
      addIssue(errors, "parserConfidence", `must be one of ${PARSER_CONFIDENCE_LEVELS.join(", ")}`);
    }
    validateParserWarnings(output.parserWarnings, "parserWarnings", errors);
    validateEngineLayout(output.engineLayout, "engineLayout", errors);
    validateEnumOrNull(output.engineFamily, "engineFamily", errors, canonicalEngineFamilySet);
    validateEnumOrNull(output.engineCooling, "engineCooling", errors, canonicalEngineCoolingTypeSet);
    validateEnumOrNull(output.engineControl, "engineControl", errors, canonicalEngineControlTypeSet);
    validateTextField(output.engineMake, "engineMake", errors);
    validateTextField(output.engineModel, "engineModel", errors);
    validateEnumOrNull(output.comboAlignment, "comboAlignment", errors, canonicalComboAlignmentSet);
    validateNumberField(output.comboOffsetSeconds, "comboOffsetSeconds", errors);
    validateSourceData(output.sourceData, points, errors);
    if (output.auxSeriesKeys !== undefined && output.auxSeriesKeys !== null) {
      if (!Array.isArray(output.auxSeriesKeys)) {
        addIssue(errors, "auxSeriesKeys", "must be an array of strings");
      } else {
        output.auxSeriesKeys.forEach((key, index) => {
          if (typeof key !== "string" || !key) addIssue(errors, `auxSeriesKeys[${index}]`, "must be a non-empty string");
        });
      }
    }
  }

  points.forEach((point, index) => {
    const result = validateCanonicalFlightPoint(point, { ...options, path: `points[${index}]` });
    errors.push(...result.errors);
    warnings.push(...result.warnings);
  });

  return {
    valid: errors.length === 0,
    schemaVersion: schemaVersion ?? CANONICAL_SCHEMA_VERSION,
    parserId: isEnvelope ? output.parserId : options.parserId ?? null,
    parserVersion: isEnvelope ? output.parserVersion : options.parserVersion ?? null,
    errors,
    warnings,
  };
}

function validateObjectField(value, path, errors, { required = false } = {}) {
  if (value === undefined) {
    if (required) addIssue(errors, path, "is required", "missing_required_field");
    return false;
  }
  if (!isPlainObject(value)) {
    addIssue(errors, path, "must be an object");
    return false;
  }
  return true;
}

function validateArrayField(value, path, errors, { required = false } = {}) {
  if (value === undefined) {
    if (required) addIssue(errors, path, "is required", "missing_required_field");
    return false;
  }
  if (!Array.isArray(value)) {
    addIssue(errors, path, "must be an array");
    return false;
  }
  return true;
}

function validateBooleanOrNull(value, path, errors, { required = false } = {}) {
  if (value === undefined) {
    if (required) addIssue(errors, path, "is required", "missing_required_field");
    return;
  }
  if (value !== null && typeof value !== "boolean") {
    addIssue(errors, path, "must be a boolean or null");
  }
}

function validateEnumOrNull(value, path, errors, options, { required = false } = {}) {
  if (value === undefined) {
    if (required) addIssue(errors, path, "is required", "missing_required_field");
    return;
  }
  if (value === null && !required) return;
  if (!options.has(value)) {
    addIssue(errors, path, `must be one of ${[...options].join(", ")}`);
  }
}

function validateEvidenceNumericStats(stats, path, errors) {
  if (!validateObjectField(stats, path, errors, { required: true })) return;
  validateTextField(stats.unit, `${path}.unit`, errors);
  validateNumberField(stats.finiteCount, `${path}.finiteCount`, errors, { required: true });
  if (!Number.isInteger(stats.finiteCount) || stats.finiteCount < 0) {
    addIssue(errors, `${path}.finiteCount`, "must be a non-negative integer");
  }
  for (const key of ["min", "avg", "max", "start", "end", "delta"]) {
    validateNumberField(stats[key], `${path}.${key}`, errors);
  }
}

function validateEvidenceStatsMap(stats, path, errors) {
  if (!validateObjectField(stats, path, errors, { required: true })) return;
  for (const [key, value] of Object.entries(stats)) {
    validateEvidenceNumericStats(value, `${path}.${key}`, errors);
  }
}

function validateEvidenceWindow(window, path, errors) {
  if (!validateObjectField(window, path, errors, { required: true })) return;
  validateNumberField(window.startTime, `${path}.startTime`, errors, { required: true });
  validateNumberField(window.endTime, `${path}.endTime`, errors);
  validateTextField(window.label, `${path}.label`, errors);
  validateTextField(window.phase, `${path}.phase`, errors);
  validateTextField(window.scope, `${path}.scope`, errors);
  validateTextField(window.engineId, `${path}.engineId`, errors);
  validateNumberField(window.sourceCylinder, `${path}.sourceCylinder`, errors);
  validateTextField(window.channelKey, `${path}.channelKey`, errors);
}

function validateEvidenceFinding(finding, path, errors) {
  if (!validateObjectField(finding, path, errors, { required: true })) return;
  validateTextField(finding.key, `${path}.key`, errors, { required: true });
  validateTextField(finding.title, `${path}.title`, errors, { required: true });
  validateEnumOrNull(finding.severity, `${path}.severity`, errors, evidenceSeveritySet);
  validateNumberField(finding.score, `${path}.score`, errors);
  validateTextField(finding.summary, `${path}.summary`, errors);
  validateEnumOrNull(finding.scope, `${path}.scope`, errors, evidenceFindingScopeSet, { required: true });
  validateTextField(finding.engineId, `${path}.engineId`, errors);
  validateTextField(finding.engineSide, `${path}.engineSide`, errors);
  validateNumberField(finding.sourceCylinder, `${path}.sourceCylinder`, errors);
  validateTextField(finding.channelKey, `${path}.channelKey`, errors);
  if (validateArrayField(finding.windows, `${path}.windows`, errors, { required: true })) {
    finding.windows.forEach((window, index) => validateEvidenceWindow(window, `${path}.windows[${index}]`, errors));
  }
}

function validateEvidenceCaveat(caveat, path, errors) {
  if (!validateObjectField(caveat, path, errors, { required: true })) return;
  validateTextField(caveat.code, `${path}.code`, errors, { required: true });
  validateEnumOrNull(caveat.severity, `${path}.severity`, errors, evidenceSeveritySet, { required: true });
  validateTextField(caveat.text, `${path}.text`, errors, { required: true });
  if (validateArrayField(caveat.evidenceKeys, `${path}.evidenceKeys`, errors, { required: true })) {
    caveat.evidenceKeys.forEach((key, index) => validateTextField(key, `${path}.evidenceKeys[${index}]`, errors, { required: true }));
  }
}

function validateEvidenceFlaggedWindow(window, path, errors) {
  if (!validateObjectField(window, path, errors, { required: true })) return;
  validateTextField(window.key, `${path}.key`, errors, { required: true });
  validateTextField(window.label, `${path}.label`, errors, { required: true });
  validateEnumOrNull(window.severity, `${path}.severity`, errors, evidenceSeveritySet, { required: true });
  validateNumberField(window.startTime, `${path}.startTime`, errors, { required: true });
  validateNumberField(window.endTime, `${path}.endTime`, errors, { required: true });
  validateTextField(window.dominantPhase, `${path}.dominantPhase`, errors);
  validateTextField(window.channelKey, `${path}.channelKey`, errors);
  validateNumberField(window.sourceCylinder, `${path}.sourceCylinder`, errors);
  validateEnumOrNull(window.source, `${path}.source`, errors, evidenceWindowSourceSet, { required: true });
  validateEvidenceStatsMap(window.stats, `${path}.stats`, errors);
  validateEvidenceStatsMap(window.contextDeltas, `${path}.contextDeltas`, errors);
  if (validateArrayField(window.interpretationHints, `${path}.interpretationHints`, errors, { required: true })) {
    window.interpretationHints.forEach((hint, index) => validateTextField(hint, `${path}.interpretationHints[${index}]`, errors, { required: true }));
  }
}

function validateEvidencePhaseContextVerdict(verdict, path, errors) {
  if (!validateObjectField(verdict, path, errors, { required: true })) return;
  for (const key of ["channelKey", "engineId", "engineSide"]) {
    validateTextField(verdict[key], `${path}.${key}`, errors);
  }
  validateNumberField(verdict.sourceCylinder, `${path}.sourceCylinder`, errors);
  for (const key of ["key", "findingKey", "windowLabel", "eventType", "verdict", "mentionPriority", "reason"]) {
    validateTextField(verdict[key], `${path}.${key}`, errors, { required: true });
  }
  for (const key of ["startTime", "endTime", "eventStartTime", "eventEndTime"]) {
    validateNumberField(verdict[key], `${path}.${key}`, errors);
  }
  for (const key of ["affectedSignals", "evidenceKeys"]) {
    if (validateArrayField(verdict[key], `${path}.${key}`, errors, { required: true })) {
      verdict[key].forEach((item, index) => validateTextField(item, `${path}.${key}[${index}]`, errors, { required: true }));
    }
  }
}

function validateEvidenceSpecialTests(specialTests, path, errors) {
  if (specialTests === undefined) return;
  if (!validateObjectField(specialTests, path, errors)) return;
  if (specialTests.gamiSweeps !== undefined && validateArrayField(specialTests.gamiSweeps, `${path}.gamiSweeps`, errors)) {
    specialTests.gamiSweeps.forEach((sweep, index) => {
      if (!validateObjectField(sweep, `${path}.gamiSweeps[${index}]`, errors)) return;
      for (const key of ["id", "testKind", "testName", "engineId", "engineLabel", "savedAt", "status", "label", "confidence", "confidenceDetail", "spreadBand", "durationBand"]) {
        validateTextField(sweep[key], `${path}.gamiSweeps[${index}].${key}`, errors);
      }
      for (const key of ["startTime", "endTime", "durationSeconds", "sampleCount", "spreadGph", "clearPeakCount", "peakCount"]) {
        validateNumberField(sweep[key], `${path}.gamiSweeps[${index}].${key}`, errors);
      }
      validateBooleanOrNull(sweep.complete, `${path}.gamiSweeps[${index}].complete`, errors);
      validateObjectField(sweep.pilotContext, `${path}.gamiSweeps[${index}].pilotContext`, errors);
    });
  }
}

function validatePilotCorrelatedEvents(events, path, errors) {
  if (events === undefined) return;
  if (!validateArrayField(events, path, errors)) return;
  events.forEach((event, index) => {
    const eventPath = `${path}[${index}]`;
    if (!validateObjectField(event, eventPath, errors)) return;
    for (const key of ["id", "testKind", "testName", "savedAt", "dominantPhase"]) {
      validateTextField(event[key], `${eventPath}.${key}`, errors);
    }
    for (const key of ["startTime", "endTime", "durationSeconds", "sampleCount", "sampleIntervalSeconds"]) {
      validateNumberField(event[key], `${eventPath}.${key}`, errors);
    }
    validateObjectField(event.observation, `${eventPath}.observation`, errors, { required: true });
    validateObjectField(event.correlation, `${eventPath}.correlation`, errors, { required: true });
    validateObjectField(event.dataCoverage, `${eventPath}.dataCoverage`, errors, { required: true });
    validateObjectField(event.powerContext, `${eventPath}.powerContext`, errors, { required: true });
    validateArrayField(event.detectorCoverage, `${eventPath}.detectorCoverage`, errors, { required: true });
    validateArrayField(event.interpretationFamilies, `${eventPath}.interpretationFamilies`, errors, { required: true });
  });
}

function validateAiEvidenceKeys(value, path, errors) {
  if (validateArrayField(value, path, errors, { required: true })) {
    value.forEach((key, index) => validateTextField(key, `${path}[${index}]`, errors, { required: true }));
  }
}

function validateAiSummaryItem(item, path, errors) {
  if (!validateObjectField(item, path, errors, { required: true })) return;
  validateTextField(item.title, `${path}.title`, errors, { required: true });
  validateTextField(item.text, `${path}.text`, errors, { required: true });
  validateAiEvidenceKeys(item.evidenceKeys, `${path}.evidenceKeys`, errors);
}

function validateAiSupportingEvidenceItem(item, path, errors) {
  if (!validateObjectField(item, path, errors, { required: true })) return;
  validateTextField(item.label, `${path}.label`, errors, { required: true });
  validateTextField(item.value, `${path}.value`, errors, { required: true });
  validateTextField(item.detail, `${path}.detail`, errors, { required: true });
  validateAiEvidenceKeys(item.evidenceKeys, `${path}.evidenceKeys`, errors);
}

function validateAiExploratoryTraceItem(item, path, errors) {
  if (!validateObjectField(item, path, errors, { required: true })) return;
  for (const key of ["channelKey", "label"]) validateTextField(item[key], `${path}.${key}`, errors, { required: true });
  validateTextField(item.unit, `${path}.unit`, errors);
  for (const key of ["min", "max", "start", "end", "delta"]) {
    validateNumberField(item[key], `${path}.${key}`, errors, { required: true });
  }
}

function validateAiExploratoryObservation(item, path, errors) {
  if (!validateObjectField(item, path, errors, { required: true })) return;
  if (!new Set(["engine", "shared"]).has(item.scope)) addIssue(errors, `${path}.scope`, "must be engine or shared");
  validateTextField(item.engineId, `${path}.engineId`, errors);
  for (const key of ["engineLabel", "title", "observation", "classification", "pattern", "phase"]) {
    validateTextField(item[key], `${path}.${key}`, errors, { required: true });
  }
  if (!new Set([
    "normal-operating-pattern",
    "operational-transition",
    "persistent-relationship",
    "data-quality-limitation",
    "unusual-unconfirmed-pattern",
  ]).has(item.classification)) addIssue(errors, `${path}.classification`, "has an unsupported value");
  if (!new Set([
    "stable-operation",
    "rise",
    "fall",
    "spike",
    "dip",
    "oscillation",
    "step-change",
    "coordinated-movement",
    "persistent-offset",
    "phase-response",
    "data-gap",
    "channel-separation",
    "channel-correlation",
    "phase-transition",
  ]).has(item.pattern)) addIssue(errors, `${path}.pattern`, "has an unsupported value");
  for (const key of ["startElapsedSeconds", "endElapsedSeconds"]) {
    validateNumberField(item[key], `${path}.${key}`, errors, { required: true });
  }
  if (validateArrayField(item.channelKeys, `${path}.channelKeys`, errors, { required: true })) {
    item.channelKeys.forEach((key, index) => validateTextField(key, `${path}.channelKeys[${index}]`, errors, { required: true }));
  }
  if (validateArrayField(item.trace, `${path}.trace`, errors, { required: true })) {
    item.trace.forEach((trace, index) => validateAiExploratoryTraceItem(trace, `${path}.trace[${index}]`, errors));
  }
}

export function validateFlightAiSummary(summary) {
  const errors = [];
  const warnings = [];
  if (!isPlainObject(summary)) {
    addIssue(errors, "summary", "must be an object");
    return { valid: false, errors, warnings };
  }
  if (summary.schemaVersion !== FLIGHT_AI_SUMMARY_SCHEMA_VERSION) {
    addIssue(errors, "schemaVersion", `must be ${FLIGHT_AI_SUMMARY_SCHEMA_VERSION}`);
  }

  if (validateObjectField(summary.generator, "generator", errors, { required: true })) {
    if (summary.generator.version !== FLIGHT_AI_SUMMARY_GENERATOR_VERSION) {
      addIssue(errors, "generator.version", `must be ${FLIGHT_AI_SUMMARY_GENERATOR_VERSION}`);
    }
    if (summary.generator.promptVersion !== FLIGHT_AI_SUMMARY_PROMPT_VERSION) {
      addIssue(errors, "generator.promptVersion", `must be ${FLIGHT_AI_SUMMARY_PROMPT_VERSION}`);
    }
    validateTextField(summary.generator.generatedAt, "generator.generatedAt", errors, { required: true });
    validateTextField(summary.generator.model, "generator.model", errors);
  }

  if (validateObjectField(summary.source, "source", errors, { required: true })) {
    for (const key of ["flightId", "evidencePacketId", "evidenceSchemaVersion"]) {
      validateTextField(summary.source[key], `source.${key}`, errors, { required: true });
    }
    validateTextField(summary.source.evidenceSha256, "source.evidenceSha256", errors);
    validateTextField(summary.source.evidenceGeneratedAt, "source.evidenceGeneratedAt", errors);
  }

  validateTextField(summary.headline, "headline", errors, { required: true });
  validateTextField(summary.overview, "overview", errors, { required: true });

  for (const key of ["whatStandsOut", "operatingContext", "caveats", "suggestedReviewFocus"]) {
    if (validateArrayField(summary[key], key, errors, { required: true })) {
      summary[key].forEach((item, index) => validateAiSummaryItem(item, `${key}[${index}]`, errors));
    }
  }

  if (validateArrayField(summary.supportingEvidence, "supportingEvidence", errors, { required: true })) {
    summary.supportingEvidence.forEach((item, index) => validateAiSupportingEvidenceItem(item, `supportingEvidence[${index}]`, errors));
  }

  if (summary.engineSummaries !== undefined && validateArrayField(summary.engineSummaries, "engineSummaries", errors)) {
    summary.engineSummaries.forEach((engineSummary, index) => {
      const path = `engineSummaries[${index}]`;
      if (!validateObjectField(engineSummary, path, errors, { required: true })) return;
      validateTextField(engineSummary.engineId, `${path}.engineId`, errors, { required: true });
      validateTextField(engineSummary.engineLabel, `${path}.engineLabel`, errors, { required: true });
      validateTextField(engineSummary.overview, `${path}.overview`, errors, { required: true });
      validateAiEvidenceKeys(engineSummary.evidenceKeys, `${path}.evidenceKeys`, errors);
      for (const key of ["whatStandsOut", "operatingContext", "suggestedReviewFocus"]) {
        if (validateArrayField(engineSummary[key], `${path}.${key}`, errors, { required: true })) {
          engineSummary[key].forEach((item, itemIndex) => validateAiSummaryItem(item, `${path}.${key}[${itemIndex}]`, errors));
        }
      }
    });
  }

  if (summary.exploratoryAnalysis !== undefined && validateArrayField(summary.exploratoryAnalysis, "exploratoryAnalysis", errors)) {
    if (summary.exploratoryAnalysis.length > 5) addIssue(errors, "exploratoryAnalysis", "must contain no more than 5 observations");
    summary.exploratoryAnalysis.forEach((item, index) => validateAiExploratoryObservation(item, `exploratoryAnalysis[${index}]`, errors));
  }

  return { valid: errors.length === 0, errors, warnings };
}

export function validateFlightEvidencePacket(packet) {
  const errors = [];
  const warnings = [];
  if (!isPlainObject(packet)) {
    addIssue(errors, "packet", "must be an object");
    return { valid: false, errors, warnings };
  }
  if (packet.schemaVersion !== FLIGHT_EVIDENCE_PACKET_SCHEMA_VERSION) {
    addIssue(errors, "schemaVersion", `must be ${FLIGHT_EVIDENCE_PACKET_SCHEMA_VERSION}`);
  }

  if (validateObjectField(packet.generator, "generator", errors, { required: true })) {
    if (packet.generator.version !== FLIGHT_EVIDENCE_GENERATOR_VERSION) {
      addIssue(errors, "generator.version", `must be ${FLIGHT_EVIDENCE_GENERATOR_VERSION}`);
    }
    validateTextField(packet.generator.generatedAt, "generator.generatedAt", errors, { required: true });
    validateTextField(packet.generator.analysisVersion, "generator.analysisVersion", errors);
  }

  if (validateObjectField(packet.sourceArtifacts, "sourceArtifacts", errors, { required: true })) {
    for (const key of ["flightId", "aircraftId", "normalizedArtifactId", "analysisRunId"]) {
      validateTextField(packet.sourceArtifacts[key], `sourceArtifacts.${key}`, errors, { required: true });
    }
    for (const key of ["uploadId", "normalizedSha256", "analysisSha256", "parserId", "parserVersion", "canonicalSchemaVersion"]) {
      validateTextField(packet.sourceArtifacts[key], `sourceArtifacts.${key}`, errors);
    }
  }

  if (validateObjectField(packet.flight, "flight", errors, { required: true })) {
    for (const key of ["title", "tailNumber", "flightDate", "flightStartAt", "flightEndAt", "sourcePath", "monitor"]) {
      validateTextField(packet.flight[key], `flight.${key}`, errors);
    }
    validateNumberField(packet.flight.durationSeconds, "flight.durationSeconds", errors);
    validateNumberField(packet.flight.pointCount, "flight.pointCount", errors, { required: true });
  }

  if (validateObjectField(packet.aircraft, "aircraft", errors, { required: true })) {
    for (const key of ["make", "model", "engineMake", "engineModel", "fuelPrimary"]) {
      validateTextField(packet.aircraft[key], `aircraft.${key}`, errors);
    }
    validateEnumOrNull(packet.aircraft.engineFamily, "aircraft.engineFamily", errors, canonicalEngineFamilySet);
    validateEnumOrNull(packet.aircraft.engineCooling, "aircraft.engineCooling", errors, canonicalEngineCoolingTypeSet);
    validateEnumOrNull(packet.aircraft.engineControl, "aircraft.engineControl", errors, canonicalEngineControlTypeSet);
    validateNumberField(packet.aircraft.engineCount, "aircraft.engineCount", errors);
    validateObjectField(packet.aircraft.turbo, "aircraft.turbo", errors, { required: true });
    validateBooleanOrNull(packet.aircraft.turbo?.isTurbo, "aircraft.turbo.isTurbo", errors, { required: true });
    if (packet.aircraft.propeller !== undefined && validateObjectField(packet.aircraft.propeller, "aircraft.propeller", errors)) {
      validateTextField(packet.aircraft.propeller.type, "aircraft.propeller.type", errors);
      if (
        packet.aircraft.propeller.type !== null
        && packet.aircraft.propeller.type !== undefined
        && !["fixed_pitch", "constant_speed"].includes(packet.aircraft.propeller.type)
      ) {
        addIssue(errors, "aircraft.propeller.type", "must be fixed_pitch, constant_speed, or null");
      }
      validateTextField(packet.aircraft.propeller.label, "aircraft.propeller.label", errors);
      if (validateArrayField(packet.aircraft.propeller.entries, "aircraft.propeller.entries", errors, { required: true })) {
        packet.aircraft.propeller.entries.forEach((entry, index) => {
          if (!validateObjectField(entry, `aircraft.propeller.entries[${index}]`, errors)) return;
          for (const key of ["engineId", "label", "value"]) {
            validateTextField(entry[key], `aircraft.propeller.entries[${index}].${key}`, errors);
          }
        });
      }
    }
  }

  if (validateObjectField(packet.overall, "overall", errors, { required: true })) {
    validateNumberField(packet.overall.score, "overall.score", errors);
    for (const key of ["label", "className", "status", "primaryFindingKey"]) {
      validateTextField(packet.overall[key], `overall.${key}`, errors);
    }
    validateNumberField(packet.overall.findingCount, "overall.findingCount", errors, { required: true });
    validateNumberField(packet.overall.metricCount, "overall.metricCount", errors, { required: true });
  }

  if (validateObjectField(packet.startupBehavior, "startupBehavior", errors, { required: true })) {
    validateTextField(packet.startupBehavior.key, "startupBehavior.key", errors, { required: true });
    validateNumberField(packet.startupBehavior.score, "startupBehavior.score", errors);
    validateTextField(packet.startupBehavior.status, "startupBehavior.status", errors);
    validateTextField(packet.startupBehavior.note, "startupBehavior.note", errors);
    validateTextField(packet.startupBehavior.startupCoverage, "startupBehavior.startupCoverage", errors, { required: true });
    validateNumberField(packet.startupBehavior.startTime, "startupBehavior.startTime", errors);
    validateNumberField(packet.startupBehavior.endTime, "startupBehavior.endTime", errors);
    validateArrayField(packet.startupBehavior.interpretation, "startupBehavior.interpretation", errors, { required: true });
    validateArrayField(packet.startupBehavior.tags, "startupBehavior.tags", errors, { required: true });
    validateArrayField(packet.startupBehavior.windows, "startupBehavior.windows", errors, { required: true });
  }

  for (const key of ["phaseSummary", "representativeWindows", "cylinderSummary", "metricSummary", "findings", "flaggedWindows", "phaseEvents", "phaseContextVerdicts", "caveats"]) {
    validateArrayField(packet[key], key, errors, { required: true });
  }
  validateObjectField(packet.baselineComparison, "baselineComparison", errors, { required: true });
  validateEvidenceSpecialTests(packet.specialTests, "specialTests", errors);
  validatePilotCorrelatedEvents(packet.pilotCorrelatedEvents, "pilotCorrelatedEvents", errors);
  packet.findings?.forEach((finding, index) => validateEvidenceFinding(finding, `findings[${index}]`, errors));
  packet.flaggedWindows?.forEach((window, index) => validateEvidenceFlaggedWindow(window, `flaggedWindows[${index}]`, errors));
  packet.phaseContextVerdicts?.forEach((verdict, index) => validateEvidencePhaseContextVerdict(verdict, `phaseContextVerdicts[${index}]`, errors));
  packet.caveats?.forEach((caveat, index) => validateEvidenceCaveat(caveat, `caveats[${index}]`, errors));

  return { valid: errors.length === 0, errors, warnings };
}
